/**
 * MSPBMeasureDetailPanel Component
 * 
 * Specialized detail view for MSPB (Medicare Spending Per Beneficiary) measure showing:
 * - Key metrics (decile, episode count, avg cost, benchmark, avg LOS)
 * - Insights (positioned right after metrics for visibility)
 * - Episode phase breakdown (horizontal bars) + Post-Discharge Disposition (table)
 * - Episode Journey visualization (flowchart style)
 * - LOS analysis with distribution
 * - High-cost episodes table (filterable by disposition and LOS)
 * 
 * @see MIPS_Cost_Dashboard_Implementation_Spec_v2_2
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  Activity,
  DollarSign,
  Clock,
  TrendingUp,
  AlertTriangle,
  Eye,
  Lightbulb,
  ChevronRight,
  X,
} from 'lucide-react';
import { MSPBPatientDetailView } from './MSPBPatientDetailView';
import { DecileBar, ScoreSubmissionOnlyView } from './CostSharedComponents';
import { type CostMeasure } from '@/lib/cost-calculator-utils';

// ============================================
// Types
// ============================================

interface MSBPMeasureDetailPanelProps {
  measure: CostMeasure;
  practiceId: string;
  npi?: string;
  onClose: () => void;
}

interface MSPBDetailData {
  metrics: {
    decile: number | null;
    episodeCount: number;
    avgCost: number;
    nationalAvg: number;
    variance: number;
    avgLOS: number;
  };
  phaseBreakdown: {
    key: string;
    label: string;
    description: string;
    totalCost: number;
    pctOfTotal: number;
  }[];
  dispositionSummary: {
    key: string;
    label: string;
    flagged: boolean;
    episodeCount: number;
    totalCost: number;
    avgCost: number;
    pctOfEpisodes: number;
  }[];
  losAnalysis: {
    avgLOS: number;
    outlierCount: number;
    outlierThreshold: number;
    distribution: {
      label: string;
      count: number;
    }[];
  };
  insights: {
    id: string;
    priority: number;
    text: string;
  }[];
  highCostEpisodes: {
    mbi: string;
    episodeId: string;
    gender: string;
    dob: string;
    dateOfDeath: string | null;
    totalCost: number;
    nationalAvg: number;
    variance: number;
    los: number;
    disposition: string;
    riskPercentile: number;
    topCostPhase: string;
    admissionDate: string;
    dischargeDate: string;
    hasSNF: boolean;
    hasReadmission: boolean;
  }[];
}

interface ActiveFilter {
  type: 'disposition' | 'los';
  value: string;
  label: string;
}

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatCurrencyFull(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatPercentInt(value: number): string {
  return `${Math.round(value)}%`;
}

function calculateAge(dob: string | null): number | null {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

// Parse LOS range from label like "1-3d", "4-7d", "8-14d", "15+d"
function parseLosRange(label: string): { min: number; max: number } {
  const cleaned = label.replace('d', '').trim();
  if (cleaned.includes('+')) {
    const min = parseInt(cleaned.replace('+', ''), 10);
    return { min, max: Infinity };
  }
  const parts = cleaned.split('-');
  if (parts.length === 2) {
    return { min: parseInt(parts[0], 10), max: parseInt(parts[1], 10) };
  }
  // Fallback for single number
  const num = parseInt(cleaned, 10);
  return { min: num, max: num };
}

function isLosInRange(los: number, label: string): boolean {
  const { min, max } = parseLosRange(label);
  return los >= min && los <= max;
}

// ============================================
// Component
// ============================================

export function MSPBMeasureDetailPanel({
  measure,
  practiceId,
  npi,
  onClose,
}: MSBPMeasureDetailPanelProps) {
  const [data, setData] = useState<MSPBDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllEpisodes, setShowAllEpisodes] = useState(false);
  const [selectedPatientMbi, setSelectedPatientMbi] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter | null>(null);

  // Load MSPB detail data (skip when score-only — no patient-level feedback files)
  useEffect(() => {
    if (measure.scoreSource === 'cms_submission_only') {
      setIsLoading(false);
      return;
    }
    loadMSPBDetail();
  }, [practiceId, npi, measure.scoreSource]);

  const loadMSPBDetail = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const headers = { Authorization: `Bearer ${token}` };

      const url = new URL(`${apiUrl}/api/practices/${practiceId}/cost/mspb/detail`);
      if (npi) url.searchParams.set('npi', npi);

      const response = await fetch(url.toString(), { headers });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error('Error loading MSPB detail:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (measure.scoreSource === 'cms_submission_only') {
    return <ScoreSubmissionOnlyView measure={measure} onClose={onClose} />;
  }

  // Filter handlers
  const handleDispositionClick = (key: string, label: string) => {
    if (activeFilter?.type === 'disposition' && activeFilter.value === key) {
      setActiveFilter(null);
    } else {
      setActiveFilter({ type: 'disposition', value: key, label });
    }
  };

  const handleLosClick = (label: string) => {
    if (activeFilter?.type === 'los' && activeFilter.value === label) {
      setActiveFilter(null);
    } else {
      setActiveFilter({ type: 'los', value: label, label: `LOS: ${label}` });
    }
  };

  const clearFilter = () => {
    setActiveFilter(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No MSPB data available</p>
        <Button variant="outline" className="mt-4" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Measures
        </Button>
      </div>
    );
  }

  // Eye → dedicated MSPB patient endpoint (/cost/patient/:mbi/mspb). The generic
  // PatientDetailModal uses getPatientData(), which never adds MSPB-only MBIs
  // (preexisting 404 "Failed to load patient details").
  if (selectedPatientMbi) {
    return (
      <MSPBPatientDetailView
        mbi={selectedPatientMbi}
        practiceId={String(practiceId)}
        npi={npi}
        onBack={() => setSelectedPatientMbi(null)}
      />
    );
  }

  const { metrics, phaseBreakdown, dispositionSummary, losAnalysis, insights, highCostEpisodes } = data;

  // Apply filter to episodes
  const filteredEpisodes = activeFilter
    ? highCostEpisodes.filter(ep => {
        if (activeFilter.type === 'disposition') {
          return ep.disposition === activeFilter.value;
        }
        if (activeFilter.type === 'los') {
          return isLosInRange(ep.los, activeFilter.value);
        }
        return true;
      })
    : highCostEpisodes;

  const displayedEpisodes = showAllEpisodes ? filteredEpisodes : filteredEpisodes.slice(0, 10);

  // Calculate max cost for bar scaling
  const maxPhaseCost = Math.max(...phaseBreakdown.map(p => p.totalCost));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={onClose} className="mb-2 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Measures
          </Button>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">MSPB Clinician</h2>
            <Badge className="bg-blue-100 text-blue-800">Population</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Medicare spending 3 days before through 30 days after acute inpatient discharge
          </p>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-5 gap-4">
        {/* Decile Score */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Decile Score</p>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold">
              {metrics.decile?.toFixed(1) ?? measure.points?.toFixed(1) ?? '—'}
            </span>
          </div>
          <div className="mt-2">
            <DecileBar decile={metrics.decile ?? measure.points ?? 1} />
          </div>
        </div>

        {/* Episodes */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Episodes</p>
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{metrics.episodeCount.toLocaleString()}</span>
          </div>
        </div>

        {/* Your Avg Cost */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Your Avg Cost</p>
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{formatCurrency(metrics.avgCost)}</span>
          </div>
          <p className={`text-xs mt-1 ${metrics.variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {metrics.variance > 0 ? '+' : ''}{formatPercent(metrics.variance)} vs benchmark
          </p>
        </div>

        {/* Benchmark */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Benchmark</p>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{formatCurrency(metrics.nationalAvg)}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">National Median</p>
        </div>

        {/* Avg LOS */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Avg LOS</p>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{metrics.avgLOS}</span>
            <span className="text-lg text-muted-foreground">days</span>
          </div>
        </div>
      </div>

      {/* Insights Row - Moved right after metrics */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="h-5 w-5 text-amber-500" />
          <h3 className="font-semibold">Insights</h3>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {insights.length > 0 ? (
            insights.map((insight) => (
              <div key={insight.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-900">{insight.text}</p>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground col-span-3">No actionable insights at this time.</p>
          )}
        </div>
      </div>

      {/* Episode Cost by Phase + Post-Discharge Disposition (Spec Layout) */}
      <div className="grid grid-cols-2 gap-6">
        {/* Episode Cost by Phase - Horizontal Bars */}
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 uppercase text-sm tracking-wide">Episode Cost by Phase</h3>
          
          <div className="space-y-3">
            {phaseBreakdown.map(phase => {
              const barWidth = maxPhaseCost > 0 ? (phase.totalCost / maxPhaseCost) * 100 : 0;
              
              return (
                <div key={phase.key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{phase.label}</span>
                    <span className="text-sm font-semibold">{formatCurrency(phase.totalCost)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                      <div 
                        className="h-full bg-gray-700 rounded transition-all duration-500"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-10 text-right">
                      {formatPercentInt(phase.pctOfTotal)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Post-Discharge Disposition - Clickable Table */}
        <div className="bg-card border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold uppercase text-sm tracking-wide">Post-Discharge Disposition</h3>
            <span className="text-xs text-muted-foreground">Click to filter</span>
          </div>
          
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-medium">Disposition</th>
                <th className="text-right py-2 font-medium">Eps</th>
                <th className="text-right py-2 font-medium">Avg Cost</th>
              </tr>
            </thead>
            <tbody>
              {dispositionSummary.map(disp => {
                const isSelected = activeFilter?.type === 'disposition' && activeFilter.value === disp.key;
                
                return (
                  <tr 
                    key={disp.key} 
                    className={`border-b last:border-b-0 cursor-pointer transition-colors ${
                      isSelected 
                        ? 'bg-blue-50' 
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleDispositionClick(disp.key, disp.label)}
                  >
                    <td className="py-2.5">
                      <span className="flex items-center gap-1.5">
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                        {disp.label}
                        {disp.flagged && (
                          <span className="text-amber-500 font-bold">!</span>
                        )}
                      </span>
                    </td>
                    <td className="py-2.5 text-right font-mono">{disp.episodeCount}</td>
                    <td className="py-2.5 text-right font-mono">{formatCurrencyFull(disp.avgCost)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          <p className="text-xs text-muted-foreground mt-3 pt-2 border-t">
            <span className="text-amber-500 font-bold">!</span> = Higher cost, review needed
          </p>
        </div>
      </div>

      {/* Episode Journey Visualization - Flowchart Style */}
      <div className="bg-card border rounded-lg p-4">
        <h3 className="font-semibold mb-4">Episode Journey Visualization</h3>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <div className="flex items-stretch gap-0">
            {/* Pre-Admit Phase */}
            <div className="flex items-center">
              <div className="border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 text-center bg-white">
                <p className="text-xs text-muted-foreground">Pre-Admit</p>
                <p className="text-sm font-semibold">(3 days)</p>
              </div>
            </div>
            
            {/* Arrow 1 */}
            <div className="flex items-center px-1">
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
            
            {/* Index Admission Phase */}
            <div className="flex items-center">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-4 py-3 text-center">
                <p className="text-xs text-muted-foreground">Index Admission</p>
                <p className="text-sm font-semibold text-blue-700">Avg {metrics.avgLOS} days</p>
              </div>
            </div>
            
            {/* Arrow 2 */}
            <div className="flex items-center px-1">
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
            
            {/* Post-Discharge Phase with Breakdown */}
            <div className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-4 bg-white">
              <p className="text-xs text-muted-foreground text-center mb-3">Post-Discharge (30 days)</p>
              
              <div className="space-y-1.5">
                {dispositionSummary.map(disp => {
                  const textColor = disp.key === 'readmitted' ? 'text-orange-600' : 'text-gray-700';
                  
                  return (
                    <div key={disp.key} className="flex items-center justify-between text-sm">
                      <span className={textColor}>{disp.label}</span>
                      <span className={`font-medium ${textColor}`}>
                        {formatPercentInt(disp.pctOfEpisodes)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LOS Distribution - Clickable */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold">LOS Distribution</h3>
            <span className="text-xs text-muted-foreground">Click to filter</span>
          </div>
          {losAnalysis.outlierCount > 0 && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              {losAnalysis.outlierCount} outliers (&gt;{losAnalysis.outlierThreshold}d)
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-4 gap-3">
          {losAnalysis.distribution.map(d => {
            const isSelected = activeFilter?.type === 'los' && activeFilter.value === d.label;
            
            return (
              <div 
                key={d.label} 
                className={`text-center p-3 rounded-lg cursor-pointer transition-colors ${
                  isSelected 
                    ? 'bg-blue-100 ring-2 ring-blue-500' 
                    : 'bg-muted/50 hover:bg-muted'
                }`}
                onClick={() => handleLosClick(d.label)}
              >
                <p className="text-2xl font-bold">{d.count}</p>
                <p className="text-xs text-muted-foreground">{d.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* High-Cost Episodes Table */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="font-semibold">Episodes (sorted by cost)</h3>
              {activeFilter && (
                <Badge 
                  variant="secondary" 
                  className="flex items-center gap-1.5 cursor-pointer hover:bg-secondary/80"
                  onClick={clearFilter}
                >
                  {activeFilter.label}
                  <X className="h-3 w-3" />
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {activeFilter 
                ? `Showing ${filteredEpisodes.length} filtered episodes`
                : `Showing ${displayedEpisodes.length} of ${highCostEpisodes.length} episodes`
              }
            </p>
          </div>
          {filteredEpisodes.length > 10 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllEpisodes(!showAllEpisodes)}
            >
              {showAllEpisodes ? 'Show Less' : 'Show All'}
            </Button>
          )}
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">MBI</th>
                <th className="text-center p-3 font-medium">Gender</th>
                <th className="text-center p-3 font-medium">DOB</th>
                <th className="text-center p-3 font-medium">Age</th>
                <th className="text-center p-3 font-medium">Deceased</th>
                <th className="text-right p-3 font-medium">HCC Risk</th>
                <th className="text-right p-3 font-medium">Total Cost</th>
                <th className="text-right p-3 font-medium">Nat'l Avg</th>
                <th className="text-right p-3 font-medium">Variance</th>
                <th className="text-center p-3 font-medium">LOS</th>
                <th className="text-center p-3 font-medium">Flags</th>
                <th className="text-center p-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedEpisodes.length > 0 ? (
                displayedEpisodes.map((ep, idx) => {
                  const age = calculateAge(ep.dob);
                  
                  return (
                    <tr key={idx} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-mono">{ep.mbi}</td>
                      <td className="p-3 text-center">{ep.gender || '—'}</td>
                      <td className="p-3 text-center text-sm">
                        {ep.dob ? new Date(ep.dob).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-3 text-center">{age ?? '—'}</td>
                      <td className="p-3 text-center text-sm">
                        {ep.dateOfDeath ? new Date(ep.dateOfDeath).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-3 text-right font-mono">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          ep.riskPercentile > 75 ? 'bg-red-100 text-red-800' :
                          ep.riskPercentile > 50 ? 'bg-amber-100 text-amber-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {ep.riskPercentile}%
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono">
                        {formatCurrency(ep.totalCost)}
                      </td>
                      <td className="p-3 text-right font-mono text-muted-foreground">
                        {formatCurrency(ep.nationalAvg)}
                      </td>
                      <td className={`p-3 text-right font-semibold ${
                        ep.variance > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {ep.variance > 0 ? '+' : ''}{formatPercent(ep.variance)}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          ep.los > losAnalysis.outlierThreshold ? 'bg-amber-100 text-amber-800' : ''
                        }`}>
                          {ep.los}d
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {ep.hasReadmission && (
                            <Badge variant="destructive" className="text-xs px-1">R</Badge>
                          )}
                          {ep.hasSNF && (
                            <Badge className="bg-amber-600 text-xs px-1">SNF</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedPatientMbi(ep.mbi)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={12} className="p-8 text-center text-muted-foreground">
                    {activeFilter ? 'No episodes match the current filter' : 'No episode data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default MSPBMeasureDetailPanel;
